import { useState } from "react";

import isNil from "lodash/isNil";
import type { LayoutChangeEvent } from "react-native";

import { ExploreResourcesOverviewCardSize } from "@/constants/Explore";
import { TextTestID } from "@/constants/TestID";
import { PreviewCardSize } from "@/ui/components/groupClasses/series/preview/GroupClassSeriesPreviewCard";
import Text from "@/ui/core/Text";
import { Box, HStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { isWeb } from "@/utils/platform.util";
import { joinTestIDs } from "@/utils/string.util";

const SupportedWidths = {
  ExploreResourcesOverviewCard: ExploreResourcesOverviewCardSize.Height,
  GroupClassSeriesPreviewCard: PreviewCardSize.Height,
} as const;

type SupportedWidth = (typeof SupportedWidths)[keyof typeof SupportedWidths];
interface SidewaysHighlightTextProps {
  text: string;
  width?: SupportedWidth;
}

const SidewaysHighlightText = ({
  text,
  width: rotatedWidth = SupportedWidths.GroupClassSeriesPreviewCard,
}: SidewaysHighlightTextProps) => {
  const [outerWidth, setOuterWidth] = useState<number | undefined>(undefined);
  const handleLayout = (event: LayoutChangeEvent) => {
    const { layout } = event.nativeEvent;
    const { height: textHeight } = layout;
    // take the text's height, set it as the outer container's width; due to the 90 degree rotation
    if (isNil(outerWidth)) {
      setOuterWidth(textHeight);
    }
  };

  // web rotates via CSS writing-mode (the inner strip's height becomes its rotated length)
  // native rotates via transform, where the outer Box takes the text's measured height as its width
  //  and the inner strip takes the card height (rotatedWidth) as its length.
  // Box and inner strip therefore need *different* widths on native
  const {
    boxWidth,
    height,
    innerWidth,
    onLayout,
    overflow,
    rotate,
    writingMode,
  } = isWeb()
    ? {
        boxWidth: undefined,
        height: rotatedWidth,
        innerWidth: undefined,
        onLayout: undefined,
        overflow: "visible" as const,
        rotate: "180deg",
        writingMode: "vertical-rl" as const,
      }
    : {
        boxWidth: outerWidth,
        height: undefined,
        innerWidth: rotatedWidth,
        onLayout: handleLayout,
        overflow: undefined,
        rotate: "-90deg",
        writingMode: undefined,
      };

  return (
    <Box alignSelf="center" overflow={overflow} width={boxWidth}>
      <HStack
        alignSelf="center"
        backgroundColor={ColorToken.Primary10}
        borderRadius={12}
        height={height}
        justifyContent="center"
        onLayout={onLayout}
        paddingHorizontal={12}
        paddingVertical={8}
        style={{ writingMode }}
        transform={[{ rotate }]}
        width={innerWidth}
      >
        <Text
          accessibilityLabel={text}
          color={ColorToken.Primary100}
          testID={joinTestIDs(TextTestID.Tag, TextTestID.Text, text)}
          variant="captionMedium"
        >
          {text}
        </Text>
      </HStack>
    </Box>
  );
};

export type { SidewaysHighlightTextProps };
export { SupportedWidths };
export default SidewaysHighlightText;
